import { Globe } from 'lucide-react';
import { logoDomain, logoUrl } from '../../lib/logo.js';
import type { ApiProjectImage } from '../../lib/projects.js';
import { AuthenticatedImage } from '../auth/authenticated-image.js';
import { EntityAvatar } from '../entity-drawer/entity-drawer-shell.js';
import { imageCreditLine, imageKindLabel, imageSourceHref } from './project-image-gallery.js';

// Justified rows: each tile keeps its image's shape and grows with it, so a row shares one height.
// Contain, not cover: a shape the tile had to bound is shown whole on the tile's background.

const ROW_HEIGHT = { drawer: 140, expanded: 180 } as const;
const MIN_RATIO = 0.5;
const MAX_RATIO = 2.5;

function shapeOf(image: ApiProjectImage): number {
  const ratio = image.width && image.height ? image.width / image.height : 4 / 3;
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio));
}

export function ProjectPhotosGrid({
  images,
  expanded,
  onOpenImage,
  onImageError,
}: {
  images: ApiProjectImage[];
  expanded: boolean;
  onOpenImage: (id: string, opener: HTMLElement) => void;
  onImageError: (id: string) => void;
}) {
  if (images.length === 0) return null;
  const rowHeight = expanded ? ROW_HEIGHT.expanded : ROW_HEIGHT.drawer;
  return (
    <ul data-photo-grid className="flex flex-wrap gap-x-3 gap-y-5">
      {images.map((image) => {
        const shape = shapeOf(image);
        const domain =
          logoDomain(image.attribution.publisher_domain) ?? image.attribution.publisher_domain;
        return (
          <li
            key={image.id}
            data-photo-tile
            className="min-w-0"
            style={{ flexGrow: shape, flexBasis: `${Math.round(shape * rowHeight)}px` }}
          >
            <button
              type="button"
              aria-label={`View ${imageKindLabel(image.kind).toLowerCase()}`}
              onClick={(event) => onOpenImage(image.id, event.currentTarget)}
              style={{ aspectRatio: shape }}
              className="group block w-full cursor-zoom-in overflow-hidden rounded-xl bg-muted outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <AuthenticatedImage
                src={image.url}
                alt={`${imageKindLabel(image.kind)} from ${image.attribution.publisher_name}`}
                className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                onError={() => onImageError(image.id)}
              />
            </button>
            <a
              href={imageSourceHref(image)}
              target="_blank"
              rel="noopener noreferrer"
              title={imageCreditLine(image)}
              className="mt-2 flex min-w-0 items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <EntityAvatar src={logoUrl(domain)} alt="" fallbackIcon={Globe} size="xs" />
              <span className="truncate">{domain}</span>
            </a>
          </li>
        );
      })}
      {/* Takes the leftover width on the last row, so a short row keeps the row height. */}
      <li aria-hidden="true" className="h-0" style={{ flexGrow: 1000, flexBasis: 0 }} />
    </ul>
  );
}
